import * as Sentry from '@sentry/react';
import axiosInstance, { resetSessionExpiry } from '@/shared/lib/axios.config';
import {
  User,
  LoginPayload,
  RegisterPayload,
  LoginResult,
  VerifyOtpPayload,
  ResendOtpPayload,
  ActivateAccountPayload,
  ActivateAccountResponse,
  ActivateAccountData,
} from '@/shared/types/user.types';
import { AUTH, USER } from '@/shared/constants/api-endpoints';

export interface AuthApiResponse {
  message: string;
  data: {
    userInfo: User;
  };
  error: null | string;
}

/**
 * Authentication response returned by login/register.
 * Note: Authentication tokens are handled via HTTP-only cookies (withCredentials: true).
 * No client-side token storage is needed or recommended for security.
 */
export interface AuthResponse {
  user: User;
}

interface MfaRequiredApiResponse {
  message: string;
  data: {
    mfaSessionId: string;
    requiresMfa: boolean;
    userInfo: {
      id: string;
      name: string;
      email: string;
      roles: string[];
    };
  };
  error: null | string;
}

export const authService = {
  login: async (payload: LoginPayload): Promise<LoginResult> => {
    const response = await axiosInstance.post<
      AuthApiResponse | MfaRequiredApiResponse
    >(AUTH.LOGIN, payload);
    // A successful login replaces any expired session — re-enable the refresh flow.
    resetSessionExpiry();
    const data = response.data.data as MfaRequiredApiResponse['data'] & {
      userInfo?: User;
    };

    if ('requiresMfa' in data && data.requiresMfa) {
      const mfaData = data as MfaRequiredApiResponse['data'];
      return {
        requiresMfa: true,
        mfaSessionId: mfaData.mfaSessionId,
        userEmail: mfaData.userInfo.email,
        userName: mfaData.userInfo.name,
      };
    }

    const authData = data as AuthApiResponse['data'];
    return { requiresMfa: false, user: authData.userInfo };
  },

  verifyOtp: async (payload: VerifyOtpPayload): Promise<AuthResponse> => {
    const response = await axiosInstance.post<AuthApiResponse>(
      AUTH.VERIFY_OTP,
      payload
    );
    resetSessionExpiry();
    return { user: response.data.data.userInfo };
  },

  resendOtp: async (payload: ResendOtpPayload): Promise<void> => {
    await axiosInstance.post(AUTH.RESEND_OTP, payload);
  },

  register: async (payload: RegisterPayload): Promise<AuthResponse> => {
    const response = await axiosInstance.post<AuthApiResponse>(
      AUTH.SIGNUP,
      payload
    );
    return { user: response.data.data.userInfo };
  },

  logout: async (): Promise<void> => {
    // Delete current session before logout if exists
    try {
      const { journalService } = await import('./journal.service');
      await journalService.deleteCurrentSession();
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'logout_session_delete' },
      });
    }

    // Clear client-side cookies
    try {
      const { clearSpeechToken } = await import('@/shared/utils/speechToken');
      clearSpeechToken();
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'logout_clear_cookies' },
      });
    }

    await axiosInstance.post(AUTH.LOGOUT);
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await axiosInstance.get<AuthApiResponse>(USER.PROFILE);
    return response.data.data.userInfo;
  },

  refreshToken: async (): Promise<void> => {
    await axiosInstance.post(AUTH.REFRESH_TOKEN);
  },

  /**
   * Activate a user account by setting password with the activation token
   */
  activateAccount: async (
    payload: ActivateAccountPayload
  ): Promise<ActivateAccountData> => {
    const response = await axiosInstance.post<ActivateAccountResponse>(
      AUTH.ACTIVATE,
      payload
    );
    return response.data.data;
  },

  changePassword: async (payload: {
    oldPassword: string;
    newPassword: string;
  }): Promise<void> => {
    await axiosInstance.post(AUTH.CHANGE_PASSWORD, payload);
  },

  requestPasswordReset: async (payload: { email: string }): Promise<void> => {
    await axiosInstance.post(AUTH.PASSWORD_RESET_REQUEST, payload);
  },

  completePasswordReset: async (payload: {
    token: string;
    newPassword: string;
  }): Promise<void> => {
    await axiosInstance.post(AUTH.PASSWORD_RESET_COMPLETE, payload);
  },
};
